import { CheckInPresenter } from '@shared/modules/check-ins';
import { JourneyPresenter } from '@shared/modules/journeys/journey.presenter';
import { Presenter } from '../../presenters/presenter';
import { BookingPresenter } from '../bookings/booking.presenter';
import { CarerContractPresenter } from '../carer-contracts/carer-contract.presenter';
import type { CarerBookingAllocation } from './carer-booking-allocation';
import { FixedRatePresenter } from '../fixed-rates/fixed-rate.presenter';

export class CarerBookingAllocationPresenter extends Presenter<CarerBookingAllocation> {
    get contract(): CarerContractPresenter {
        return this.present('contract.data', CarerContractPresenter);
    }

    get checkIn(): CheckInPresenter {
        return this.present('check_in.data', CheckInPresenter);
    }

    get booking(): BookingPresenter {
        return this.present('booking.data', BookingPresenter);
    }

    get netPay(): number {
        return this._get('netPay', () => {
            const lineItems = this.get('carer_line_items.data');

            if (!Array.isArray(lineItems)) {
                return;
            }

            return lineItems.map(_ => _.net_amount).reduce((a, b) => a + b, 0);
        });
    }

    /**
     * @return
     */
    get isAppUpToDate(): boolean {
        return this.data.is_app_up_to_date
            ?? this.data.sync_status?.data?.has_latest_changes;
    }

    get syncedAt() {
        return this.toDateTime('synced_at')
            ?? this.toDateTime('sync_status.data.last_synced_at');
    }

    get syncedAtTimestamp() {
        return this._get('syncedAt', () => this.formatTimestamp(this.syncedAt));
    }

    get previousAllocation(): CarerBookingAllocationPresenter {
        return this.present('previous_allocation.data', CarerBookingAllocationPresenter);
    }

    get previousBooking(): BookingPresenter {
        return this.present('previous_booking.data', BookingPresenter);
    }

    get secondsBetweenPreviousBooking(): number {
        return this._get('secondsBetweenPreviousBooking', () => {
            const booking = this.booking;
            const previousBooking = this.previousBooking || this.previousAllocation?.booking;

            if (booking && previousBooking) {
                return booking.start.diff(previousBooking.finish, 'seconds').seconds;
            }

            return null;
        });
    }

    get sufficientGapFromPreviousBooking(): boolean {
        return this._get('sufficientGapFromPreviousBooking', () => {
            const seconds = this.secondsBetweenPreviousBooking;
            const journeyBackward = this.journeyBackward;

            if (seconds && journeyBackward) {
                return seconds > journeyBackward.durationSeconds;
            }

            return true;
        });
    }

    get gapBetweenPreviousBooking(): string {
        return this._get('gapBetweenPreviousBooking', () => {
            const seconds = this.secondsBetweenPreviousBooking;

            if (seconds) {
                return this.formatDuration(seconds);
            }

            return null;
        });
    }

    get journeyBackward(): JourneyPresenter {
        return this.present('journey_backward.data.journey.data', JourneyPresenter);
    }

    get fixedRate(): FixedRatePresenter {
        return this.present('contract_fixed_rate.data', FixedRatePresenter);
    }
}
